import { TableCard } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import type { PeriodSummary, QualityBand } from "../flag-qa-types";
import { QUALITY_BAND_COLORS } from "../flag-qa-types";
import { formatTimeToAction } from "../flag-qa-utils";

const QUALITY_BAND_LABELS: Record<QualityBand, string> = {
  low: "Low",
  moderate: "Moderate",
  high: "High",
};

const QUALITY_BAND_BADGE_COLORS: Record<QualityBand, "error" | "warning" | "success"> = {
  low: "error",
  moderate: "warning",
  high: "success",
};

interface FlagQaKpiCardsProps {
  summary: PeriodSummary;
}

export function FlagQaKpiCards({ summary }: FlagQaKpiCardsProps) {
  return (
    <div className="flex flex-col gap-4">
      {/* Row 1: Flag counts */}
      <div className="grid grid-cols-4 gap-4">
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-1">Total Flags</div>
          <div className="text-3xl font-bold text-primary">{summary.totalFlags}</div>
        </TableCard.Root>
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-1">Reviewed Flags</div>
          <div className="text-3xl font-bold text-green-500">{summary.reviewedFlags}</div>
          <div className="text-xs text-tertiary mt-1">
            {summary.totalFlags > 0 ? ((summary.reviewedFlags / summary.totalFlags) * 100).toFixed(1) : 0}% of total
          </div>
        </TableCard.Root>
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-1">Unreviewed Flags</div>
          <div className="text-3xl font-bold text-yellow-500">{summary.unreviewedFlags}</div>
        </TableCard.Root>
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-1">Avg. Time to Action</div>
          <div className="text-3xl font-bold text-primary">{formatTimeToAction(summary.avgTimeToActionHours)}</div>
        </TableCard.Root>
      </div>

      {/* Row 2: Classification counts */}
      <div className="grid grid-cols-3 gap-4">
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-1">False Positives (FP)</div>
          <div className="text-3xl font-bold text-red-400">{summary.fp}</div>
          <div className="text-xs text-tertiary mt-1">flag_dismissed</div>
        </TableCard.Root>
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-1">True Positives (TP)</div>
          <div className="text-3xl font-bold text-green-500">{summary.tp}</div>
          <div className="text-xs text-tertiary mt-1">flag_is_correct</div>
        </TableCard.Root>
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-1">False Negatives (FN)</div>
          <div className="text-3xl font-bold text-red-500">{summary.fn}</div>
          <div className="text-xs text-tertiary mt-1">add_flag</div>
        </TableCard.Root>
      </div>

      {/* Row 3: Quality metrics */}
      <div className="grid grid-cols-4 gap-4">
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-1">Recall</div>
          <div className="text-3xl font-bold text-primary">{summary.recall.toFixed(1)}%</div>
          <div className="text-xs text-tertiary mt-1">TP / (TP + FN)</div>
        </TableCard.Root>
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-1">Precision</div>
          <div className="text-3xl font-bold text-primary">{summary.precision.toFixed(1)}%</div>
          <div className="text-xs text-tertiary mt-1">TP / (TP + FP)</div>
        </TableCard.Root>
        <TableCard.Root className="p-4">
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm text-tertiary">F1 Score</div>
            <Badge color={QUALITY_BAND_BADGE_COLORS[summary.qualityBand]} size="sm">
              {QUALITY_BAND_LABELS[summary.qualityBand]}
            </Badge>
          </div>
          <div className="text-3xl font-bold" style={{ color: QUALITY_BAND_COLORS[summary.qualityBand] }}>
            {summary.f1.toFixed(1)}%
          </div>
        </TableCard.Root>
        <TableCard.Root className="p-4">
          <div className="text-sm text-tertiary mb-1">Accuracy</div>
          <div className="text-3xl font-bold text-primary">{summary.accuracy.toFixed(1)}%</div>
          <div className="text-xs text-tertiary mt-1">TP / (TP + FP + FN)</div>
        </TableCard.Root>
      </div>
    </div>
  );
}
